import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCurrentTenantSlug } from '@/utils/tenant-utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, ArrowLeft, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth-context';

interface VerificationResult {
  success: boolean;
  type?: 'signup' | 'invitation';
  user_id?: string;
  signup_data?: any;
  invitation_id?: string;
  error?: string;
}

export const VerifyCode = () => {
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [verificationType, setVerificationType] = useState<'signup' | 'invitation'>('signup');
  const [invitationId, setInvitationId] = useState<string | null>(null);
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();
  
  // Get parameters from URL
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const typeParam = searchParams.get('type') as 'signup' | 'invitation';
    const invitationIdParam = searchParams.get('invitationId');
    const returnUrlParam = searchParams.get('returnUrl');
    
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
    
    if (typeParam) {
      setVerificationType(typeParam);
    }
    
    if (invitationIdParam) {
      setInvitationId(invitationIdParam);
    }
    
    if (returnUrlParam) {
      setReturnUrl(decodeURIComponent(returnUrlParam));
    }
  }, [searchParams]);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !code) {
      setError('Please enter both email and verification code');
      return;
    }

    if (code.length !== 6) {
      setError('Verification code must be 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call the verify-code edge function
      // Get Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(`${supabaseUrl}/functions/v1/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase(),
          code: code,
          type: verificationType,
          invitationId: invitationId
        })
      });

      const result: VerificationResult = await response.json();

      if (!result.success) {
        setError(result.error || 'Invalid or expired verification code');
        return;
      }

      setSuccess(true);

      // Handle different verification types
      if (result.type === 'signup') {
        // For signup verification, try to log in the user
        console.log('🎯 Direct signup verification successful, attempting auto-login');
        
        try {
          // Get the password from signup_data if available
          let password = '';
          if (result.signup_data && result.signup_data.password) {
            password = result.signup_data.password;
          }
          
          if (password) {
            // Try to sign in the user
            console.log('🔐 Attempting auto sign-in for direct signup...');
            const { error: signInError } = await signIn(email.toLowerCase(), password);
            
            if (signInError) {
              console.error('Auto sign-in failed:', signInError);
              // Fallback to auth page
              setTimeout(() => {
                navigate('/auth?verified=true');
              }, 2000);
            } else {
              console.log('✅ Auto sign-in successful for direct signup');
              
              // Redirect to central domain and let the normal workspace check handle it
              // The user will have no workspaces, so the modal should show automatically
              // Longer delay to ensure auth context fully processes
              setTimeout(() => {
                const tenantSlug = getCurrentTenantSlug();
                const redirectPath = tenantSlug ? `/?tenant=${tenantSlug}` : '/';
                navigate(redirectPath);
              }, 3000);
            }
          } else {
            // No password available, fallback to auth page
            console.log('❌ No password available for auto-login');
            setTimeout(() => {
              navigate('/auth?verified=true');
            }, 2000);
          }
        } catch (error) {
          console.error('Direct signup verification error:', error);
          // Fallback to auth page
          setTimeout(() => {
            navigate('/auth?verified=true');
          }, 2000);
        }
        
      } else if (result.type === 'invitation') {
        // For invitation verification, try to log in the user and then redirect
        console.log('🎯 Invitation verification successful, redirecting back to invitation page');
        console.log('Return URL:', returnUrl);
        console.log('Invitation ID:', invitationId);
        
        try {
          // Get the password from signup_data if available
          let password = '';
          if (result.signup_data && result.signup_data.password) {
            password = result.signup_data.password;
          }
          
          if (password) {
            // Try to sign in the user
            console.log('🔐 Attempting auto sign-in...');
            const { error: signInError } = await signIn(email.toLowerCase(), password);
            
            if (signInError) {
              console.error('Auto sign-in failed:', signInError);
              // Continue with redirect even if auto-login fails
            } else {
              console.log('✅ Auto sign-in successful');
            }
          }
          
          // Redirect back to invitation page
          if (returnUrl) {
            console.log('🔄 Redirecting to returnUrl:', returnUrl);
            setTimeout(() => {
              // Extract the path from the full URL
              let redirectPath = returnUrl;
              try {
                const url = new URL(returnUrl);
                redirectPath = url.pathname + url.search;
              } catch (e) {
                // If returnUrl is already a path, use it as is
                redirectPath = returnUrl;
              }
              const finalUrl = redirectPath + (redirectPath.includes('?') ? '&' : '?') + 'verified=true';
              console.log('🎯 Final redirect URL:', finalUrl);
              navigate(finalUrl);
            }, 2000);
          } else {
            // Fallback to invitation page if no return URL
            console.log('🔄 No returnUrl, redirecting to /invite');
            setTimeout(() => {
              navigate('/invite?verified=true');
            }, 2000);
          }
        } catch (error) {
          console.error('Invitation verification error:', error);
          // Still redirect even if auto-login fails
          if (returnUrl) {
            setTimeout(() => {
              let redirectPath = returnUrl;
              try {
                const url = new URL(returnUrl);
                redirectPath = url.pathname + url.search;
              } catch (e) {
                redirectPath = returnUrl;
              }
              const finalUrl = redirectPath + (redirectPath.includes('?') ? '&' : '?') + 'verified=true';
              console.log('🎯 Error fallback redirect URL:', finalUrl);
              navigate(finalUrl);
            }, 2000);
          } else {
            setTimeout(() => {
              navigate('/invite?verified=true');
            }, 2000);
          }
        }
      }

    } catch (error) {
      console.error('Verification error:', error);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      // For now, show a message to contact support
      // TODO: Implement resend functionality in edge functions
      setError('To resend your verification code, please contact support or try signing up again.');
    } catch (error) {
      setError('Failed to resend verification code');
    } finally {
      setResendLoading(false);
    }
  };

  const getTitle = () => {
    if (verificationType === 'invitation') {
      return 'Verify Your Email for Invitation';
    }
    return 'Verify Your Email';
  };

  const getDescription = () => {
    if (verificationType === 'invitation') {
      return 'Enter the 6-digit verification code sent to your email to accept the invitation';
    }
    return 'Enter the 6-digit verification code sent to your email address';
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-green-600">Verification Successful!</CardTitle>
            <CardDescription>
              Your email has been verified. Redirecting you now...
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle>{getTitle()}</CardTitle>
          <CardDescription>
            {getDescription()}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleVerifyCode} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-widest font-mono"
                required
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground text-center">
                Enter the 6-digit code from your email
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || code.length !== 6}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </Button>

            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Didn't receive the code?
              </p>
              <Button
                type="button"
                variant="link"
                onClick={handleResendCode}
                disabled={resendLoading}
                className="text-sm"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Code'
                )}
              </Button>
            </div>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => navigate('/auth')}
                className="text-sm gap-1"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to Sign In
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}; 